// ========== UPSELL CART GUARD ==========
// Ensures upsell add-on items are removed when their primary item is removed
// Load this script site-wide (theme.liquid or layout)

(function () {
  "use strict";

  const UPSELL_PROPERTY_KEY = "_Upsell Add-on";
  const PRIMARY_REF_KEY = "_Primary Order Ref";
  let isProcessing = false;

  // ====== FETCH CURRENT CART ======
  async function getCart() {
    const res = await fetch("/cart.js", {
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
    });
    return res.json();
  }

  // ====== REMOVE A CART ITEM BY ITS LINE KEY ======
  async function removeCartItem(lineKey) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
      }),
    });
    return res.json();
  }

  // ====== CORE: CHECK FOR ORPHANED UPSELL ITEMS ======
  async function removeOrphanedUpsells() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await getCart();
      const items = cart.items || [];

      // Separate primary items and upsell items
      const primaryItemKeys = new Set();
      const upsellItems = [];

      items.forEach((item) => {
        const props = item.properties || {};
        if (props[UPSELL_PROPERTY_KEY] === "Yes") {
          upsellItems.push(item);
        } else {
          // This is a primary (non-upsell) item — track its key AND id
          primaryItemKeys.add(String(item.key));
          primaryItemKeys.add(String(item.id));
          // Also add the variant_id in case that's what was stored
          primaryItemKeys.add(String(item.variant_id));
        }
      });

      // Find orphaned upsells (their primary ref no longer exists in cart)
      const orphans = upsellItems.filter((upsellItem) => {
        const primaryRef = upsellItem.properties?.[PRIMARY_REF_KEY];
        if (!primaryRef) return true; // No ref = orphan, remove it
        return !primaryItemKeys.has(String(primaryRef));
      });

      // Remove orphans one by one
      if (orphans.length > 0) {
        console.log(
          `🧹 Removing ${orphans.length} orphaned upsell item(s)...`
        );

        for (const orphan of orphans) {
          console.log(
            `   Removing: ${orphan.properties?.Material || orphan.title} (key: ${orphan.key})`
          );
          await removeCartItem(orphan.key);
        }

        // Refresh the cart UI after removals
        await refreshCartUI();
      }
    } catch (err) {
      console.error("Upsell Cart Guard error:", err);
    } finally {
      isProcessing = false;
    }
  }

  // ====== REFRESH CART UI (mini-cart drawer + cart count) ======
  async function refreshCartUI() {
    try {
      const cart = await getCart();

      // Update cart count badge
      const cartCountEl = document.querySelector("cart-count");
      if (cartCountEl) {
        cartCountEl.textContent = cart.item_count;
      }

      // Refresh mini-cart HTML
      const timestamp = new Date().getTime();
      const sectionHtml = await fetch(
        `/cart?section_id=mini-cart&t=${timestamp}`
      ).then((res) => res.text());

      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = sectionHtml;
      const miniCartHTML = fakeDiv.querySelector("cart-drawer")?.outerHTML;

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && miniCartHTML) {
        cartDrawer.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: {
                ...cart,
                sections: {
                  "mini-cart": miniCartHTML,
                },
              },
              openMiniCart: false, // Don't re-open, just refresh content
            },
          })
        );

        // Re-attach our observer after cart HTML is replaced
        setTimeout(() => observeCartDrawer(), 300);
      }

      // If we're on the /cart page, reload to reflect changes
      if (window.location.pathname === "/cart") {
        window.location.reload();
      }
    } catch (err) {
      console.error("Cart UI refresh error:", err);
    }
  }

  // ====== OBSERVE CART DRAWER FOR ITEM REMOVALS ======
  function observeCartDrawer() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    // Watch for DOM changes inside the cart drawer
    // When an item is removed, the drawer re-renders → we check for orphans
    const drawerObserver = new MutationObserver((mutations) => {
      // Debounce: only run after mutations settle
      clearTimeout(drawerObserver._timeout);
      drawerObserver._timeout = setTimeout(() => {
        removeOrphanedUpsells();
      }, 500);
    });

    drawerObserver.observe(cartDrawer, {
      childList: true,
      subtree: true,
    });
  }

  // ====== INTERCEPT FETCH CALLS TO /cart/change.js ======
  // This catches ALL cart modifications (remove, quantity change)
  // regardless of which theme component triggers them
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const result = originalFetch.apply(this, args);

    const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";

    if (
      url.includes("/cart/change") ||
      url.includes("/cart/update") ||
      url.includes("/cart/clear")
    ) {
      // After the cart modification completes, check for orphaned upsells
      result.then(() => {
        // Small delay to let Shopify process the change
        setTimeout(() => {
          removeOrphanedUpsells();
        }, 800);
      });
    }

    return result;
  };

  // ====== ALSO INTERCEPT XMLHttpRequest (some themes use this) ======
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._cartGuardUrl = url;
    return originalXHROpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (
      this._cartGuardUrl &&
      (this._cartGuardUrl.includes("/cart/change") ||
        this._cartGuardUrl.includes("/cart/update"))
    ) {
      this.addEventListener("load", () => {
        setTimeout(() => {
          removeOrphanedUpsells();
        }, 800);
      });
    }
    return originalXHRSend.apply(this, args);
  };

  // ====== INITIALIZE ======
  // Run on page load in case cart already has orphans
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      removeOrphanedUpsells();
      observeCartDrawer();
    });
  } else {
    removeOrphanedUpsells();
    observeCartDrawer();
  }
})();
